/**
 * Input helpers only. The backend normalizes to E.164 with its supported
 * phone-number handling and is the authority on what is valid.
 */
export interface Country {
  iso: string;
  dial: string;
  name: string;
}

export const COUNTRIES: Country[] = [
  { iso: 'US', dial: '1', name: 'United States' },
  { iso: 'CA', dial: '1', name: 'Canada' },
  { iso: 'GB', dial: '44', name: 'United Kingdom' },
  { iso: 'IE', dial: '353', name: 'Ireland' },
  { iso: 'IN', dial: '91', name: 'India' },
  { iso: 'AU', dial: '61', name: 'Australia' },
  { iso: 'NZ', dial: '64', name: 'New Zealand' },
  { iso: 'DE', dial: '49', name: 'Germany' },
  { iso: 'FR', dial: '33', name: 'France' },
  { iso: 'ES', dial: '34', name: 'Spain' },
  { iso: 'IT', dial: '39', name: 'Italy' },
  { iso: 'NL', dial: '31', name: 'Netherlands' },
  { iso: 'BE', dial: '32', name: 'Belgium' },
  { iso: 'CH', dial: '41', name: 'Switzerland' },
  { iso: 'SE', dial: '46', name: 'Sweden' },
  { iso: 'NO', dial: '47', name: 'Norway' },
  { iso: 'DK', dial: '45', name: 'Denmark' },
  { iso: 'PL', dial: '48', name: 'Poland' },
  { iso: 'PT', dial: '351', name: 'Portugal' },
  { iso: 'AE', dial: '971', name: 'United Arab Emirates' },
  { iso: 'SA', dial: '966', name: 'Saudi Arabia' },
  { iso: 'SG', dial: '65', name: 'Singapore' },
  { iso: 'MY', dial: '60', name: 'Malaysia' },
  { iso: 'PH', dial: '63', name: 'Philippines' },
  { iso: 'JP', dial: '81', name: 'Japan' },
  { iso: 'KR', dial: '82', name: 'South Korea' },
  { iso: 'CN', dial: '86', name: 'China' },
  { iso: 'HK', dial: '852', name: 'Hong Kong' },
  { iso: 'BR', dial: '55', name: 'Brazil' },
  { iso: 'MX', dial: '52', name: 'Mexico' },
  { iso: 'AR', dial: '54', name: 'Argentina' },
  { iso: 'ZA', dial: '27', name: 'South Africa' },
  { iso: 'NG', dial: '234', name: 'Nigeria' },
  { iso: 'KE', dial: '254', name: 'Kenya' },
  { iso: 'IL', dial: '972', name: 'Israel' },
  { iso: 'PK', dial: '92', name: 'Pakistan' },
  { iso: 'BD', dial: '880', name: 'Bangladesh' },
];

export function defaultCountry(): Country {
  try {
    const region = new Intl.Locale(navigator.language).maximize().region;
    const match = COUNTRIES.find((c) => c.iso === region);
    if (match) return match;
  } catch {
    /* fall through */
  }
  return COUNTRIES[0] as Country;
}

export function digitsOnly(value: string): string {
  return value.replace(/\D/g, '');
}

/** Loose client check; E.164 allows at most 15 digits including the country code. */
export function plausibleNumber(country: Country, national: string): boolean {
  const digits = digitsOnly(national);
  return digits.length >= 4 && digits.length + country.dial.length <= 15;
}

const USERNAME = /^[a-z][a-z0-9._-]{2,29}$/;

export function usernameShapeError(value: string): string | null {
  if (!value) return null;
  if (value.length < 3) return 'Use at least 3 characters.';
  if (value.length > 30) return 'Use 30 characters or fewer.';
  if (!USERNAME.test(value)) return 'Start with a letter; use lowercase letters, numbers, dots, dashes or underscores.';
  return null;
}

/** Derived from the name only — never from the phone number or email address. */
export function suggestUsername(fullName: string): string {
  const base = fullName
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .join('.');
  if (!base || !/^[a-z]/.test(base)) return '';
  return base.slice(0, 24);
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
}
